import { useEffect, useState } from 'react';
import { useContent } from '../ContentContext';
import { submitEnquiry } from '../api';

// Home popup — admin-controlled offer image shown once per browser session when
// a user opens the website. Unchanged; separate from the sample popup below.
export function OfferPopup() {
  const { popup } = useContent();
  const [show, setShow] = useState(false);

  const enabled = !!popup?.enabled && !!popup?.image;

  useEffect(() => {
    if (!enabled) return undefined;
    if (sessionStorage.getItem('iao_offer_seen')) return undefined;
    const t = setTimeout(() => {
      setShow(true);
      sessionStorage.setItem('iao_offer_seen', '1');
    }, 1200);
    return () => clearTimeout(t);
  }, [enabled]);

  if (!enabled || !show) return null;
  return (
    <div className="overlay" onClick={() => setShow(false)}>
      <div className="popup-shell" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setShow(false)} aria-label="Close">
          <i className="fas fa-xmark" />
        </button>
        <img src={popup.image} alt="Special offer" />
      </div>
    </div>
  );
}

// "Get Free Sample" popup — a SEPARATE admin-uploaded image with its own
// redirect link, shown when the header button is clicked. Recommended size:
// 768 × 512 px (3:2 ratio).
export function SamplePopup({ open, image, link, onClose }) {
  if (!open || !image) return null;
  const img = <img src={image} alt="Get free sample" />;
  return (
    <div className="overlay" onClick={onClose}>
      <div className="sample-shell" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <i className="fas fa-xmark" />
        </button>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" onClick={onClose}>{img}</a>
        ) : img}
      </div>
    </div>
  );
}

// "Get Free Sample" enquiry modal, with the same simple maths captcha.
const BLANK = { name: '', phone: '', email: '', message: '', captcha: '' };

export function EnquiryModal({ open, onClose }) {
  const [form, setForm] = useState(BLANK);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setForm(BLANK); setMsg(null); }
  }, [open]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setMsg({ type: 'err', text: 'Please enter your name and phone number.' });
      return;
    }
    if (form.captcha.trim() !== '4') {
      setMsg({ type: 'err', text: 'Incorrect answer to the verification question.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await submitEnquiry({
        name: form.name,
        phone: form.phone,
        email: form.email,
        subject: 'Free Sample Request',
        message: form.message,
        source: 'sample',
      });
      setForm(BLANK);
      setMsg({ type: 'ok', text: 'Thank you! Your request has been sent — our team will contact you shortly.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <i className="fas fa-xmark" />
        </button>
        <h3>Get Free Sample</h3>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="field">
              <label>Name *</label>
              <input value={form.name} onChange={set('name')} placeholder="Your name" />
            </div>
            <div className="field">
              <label>Phone *</label>
              <input value={form.phone} onChange={set('phone')} placeholder="Your phone" />
            </div>
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
          </div>
          <div className="field">
            <label>Message</label>
            <textarea rows={3} value={form.message} onChange={set('message')} placeholder="Tell us your requirement" />
          </div>
          <div className="field">
            <label>2 + 2 = ?</label>
            <input value={form.captcha} onChange={set('captcha')} placeholder="Answer" />
          </div>
          <button className="consult-btn" style={{ width: '100%' }} type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Submit Enquiry'}
          </button>
          {msg ? <p className={`form-msg ${msg.type}`}>{msg.text}</p> : null}
        </form>
      </div>
    </div>
  );
}
